/**
 * PortfoliYo Lab — the Strategies section (SDO-1, restored at LAR-1).
 *
 * The visible half of the ownership change: strategies are written here now, not in Trade.
 *
 * **Not a copy of `pages/Strategies.tsx`.** That page was 1,552 lines and sixteen API calls, of
 * which nine were authoring or research (here now) and five were deployment, execution or
 * operations (still Trade's — allocation, regime multipliers, regime backfill, target-weight
 * preview). LAR-1 went back through it feature by feature
 * (`docs/operations/lab-authoring-feature-matrix.md`) and restored the authoring half it had left
 * behind: a source selector, file upload, metadata autofill, the whole portfolio workflow. The five
 * deployment features are still absent, and are absent by decision rather than by not having got to
 * them yet.
 *
 * **This file is the list.** Everything about writing a strategy lives in `strategies/`, where the
 * source-mode branch, the debounced portfolio check and the version history each have somewhere to
 * be that is not the middle of a page component.
 *
 * **What is deliberately not here:**
 *
 * * **Allocation.** Displayed, never edited — it is Trade's (decision SDO-D4), and the API client's
 *   types make writing it unrepresentable rather than merely discouraged.
 * * **Backtest / walk-forward / attribution.** They already have their own Lab pages; this links to
 *   them rather than embedding a fourth copy of each.
 * * **Any call to Trade.** Export produces a file. A person carries it. That is the entire
 *   transport, and it is what keeps a research outage from stopping trading.
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { labStrategyApi, type LabStrategy } from './labStrategyApi';
import { StrategyEditor } from './strategies/StrategyEditor';
import { statusTone } from './strategies/VersionHistory';

/** DSL or Portfolio, on the row — needed the moment there is more than one kind of strategy. */
function SourceBadge({ sourceMode }: { sourceMode: string }) {
  const portfolio = sourceMode === 'portfolio';
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
        portfolio ? 'bg-violet-50 text-violet-800' : 'bg-sky-50 text-sky-800'
      }`}
    >
      {portfolio ? 'Portfolio' : 'DSL'}
    </span>
  );
}

export function LabStrategies() {
  const [strategies, setStrategies] = useState<LabStrategy[]>([]);
  const [selected, setSelected] = useState<LabStrategy | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const payload = await labStrategyApi.list();
      setStrategies(payload.symphonies ?? []);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load strategies');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const open = useCallback(async (strategy: LabStrategy) => {
    setError(null);
    setCreating(false);
    // Fetched rather than taken from the list row: the list deliberately omits `composer_code`, and
    // an editor opened on a truncated definition would save that truncation back. So does this read
    // (#418) — it answers with the strategy described, its size and an excerpt, and the editor
    // fetches the text itself only when somebody opens it on a definition small enough to edit.
    setSelected(await labStrategyApi.get(strategy.id));
  }, []);

  const startCreating = () => {
    setCreating(true);
    setSelected(null);
    setError(null);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
      <aside>
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-slate-900">Strategies</h1>
          <button
            type="button"
            onClick={startCreating}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-800"
          >
            <Plus className="h-4 w-4" aria-hidden="true" /> New
          </button>
        </div>

        <ul className="space-y-1">
          {strategies.map((strategy) => (
            <li key={strategy.id}>
              <button
                type="button"
                onClick={() => open(strategy)}
                className={`w-full rounded-lg px-3 py-2 text-left text-sm ${
                  selected?.id === strategy.id ? 'bg-emerald-50 text-emerald-900' : 'hover:bg-slate-100'
                }`}
              >
                <span className="block font-medium">{strategy.name || strategy.symphony_id}</span>
                <span className="mt-1 flex flex-wrap items-center gap-1">
                  <SourceBadge sourceMode={strategy.source_mode} />
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${statusTone(strategy.validation_status)}`}
                  >
                    {strategy.validation_status ?? 'unvalidated'}
                  </span>
                </span>
              </button>
            </li>
          ))}
          {strategies.length === 0 && (
            <li className="px-3 py-2 text-sm text-slate-500">No strategies yet.</li>
          )}
        </ul>

        {error && (
          <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
      </aside>

      <section>
        {!selected && !creating ? (
          <p className="text-sm text-slate-500">Select a strategy, or create one.</p>
        ) : (
          <StrategyEditor
            // Remounts when the subject changes, so a half-typed definition cannot survive into a
            // different strategy's editor.
            key={selected?.id ?? 'new'}
            strategy={selected}
            onCreated={async (created) => {
              await refresh();
              await open(created);
            }}
            onSaved={async () => {
              await refresh();
              if (selected) await open(selected);
            }}
            onDeactivated={async () => {
              setSelected(null);
              await refresh();
            }}
          />
        )}
      </section>
    </div>
  );
}
